import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { type DateRange } from "@src/appV2/lib";
import { getDaylightSavingsTimeIndicator } from "@src/appV2/redesign/Shift/utils";
import { Text } from "@src/appV2/redesign/system/ui-components";

import { DurationWithBreakDisplay } from "../../components/DurationWithBreakDisplay";
import { TimeRangeLabel } from "../../DateTime/TimeRangeLabel";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { WorkplaceNameWithDistance } from "./WorkplaceNameWithDistance";

export interface ShiftSummaryInfoProps {
  dateRange: DateRange;
  deviceTimezone: string;
  durationInHours?: number;
  breakDurationInMinutes?: number;
  requiresBreak?: boolean;
  workplaceName?: string;
  formattedDistance?: string;
  timezone?: string;
  sx?: SxProps<Theme>;
  showTimeRange?: boolean;
  /**
   * Minutes elapsed since the start of the shift
   * If greater than 0, the shift start time will be displayed as "Now"
   */
  minutesElapsed?: number;
  /**
   * This field is added to overcome the issue that shift invite api does not return the break duration
   * and we need to show the break info for the shift invite card.
   *
   * It should be removed after shift invite API is updated
   */
  showBreakInfo?: boolean;
  showDay?: boolean;
  boldDay?: boolean;
}

/**
 * Renders summary about the open shift
 * - Date of the shift
 * - Time of the shift with duration
 * - Workplace name and distance
 *
 * Also includes daylight savings time indicator if applicable
 */
export function ShiftCardSummaryInfo(props: ShiftSummaryInfoProps) {
  const {
    dateRange,
    timezone,
    deviceTimezone,
    durationInHours,
    breakDurationInMinutes = 0,
    requiresBreak = false,
    workplaceName,
    formattedDistance,
    sx,
    showTimeRange = true,
    showBreakInfo = true,
    minutesElapsed = 0,
    showDay = true,
    boldDay = false,
  } = props;

  const daylightSavingsTimeIndicator = getDaylightSavingsTimeIndicator({
    shift: { start: dateRange.startDate.toISOString(), end: dateRange.endDate.toISOString() },
    timeZone: timezone ?? "America/Los_Angeles",
  });

  return (
    <Stack gap={1} sx={sx}>
      {showDay && (
        <Text bold={boldDay} variant="body2">
          {formatDayOfMonthWithDayName(dateRange.startDate, { timezone })}
        </Text>
      )}

      {showTimeRange && (
        <Stack
          spacing={1}
          direction={showBreakInfo ? "column" : "row"}
          sx={{ alignItems: showBreakInfo ? "flex-start" : "baseline" }}
        >
          <TimeRangeLabel
            semibold
            dateRange={dateRange}
            timezone={timezone}
            deviceTimezone={deviceTimezone}
            variant="body1"
            useNowForStartDate={minutesElapsed > 0}
          />

          <DurationWithBreakDisplay
            totalDurationInHours={durationInHours ?? 0}
            breakDurationInMinutes={breakDurationInMinutes}
            requiresBreak={requiresBreak}
            showBreakInfo={showBreakInfo}
            minutesElapsed={minutesElapsed}
          />
        </Stack>
      )}

      {isDefined(daylightSavingsTimeIndicator) && (
        <Text variant="body2" color={(theme) => theme.palette.text.tertiary}>
          {daylightSavingsTimeIndicator}
        </Text>
      )}

      {isDefined(workplaceName) && (
        <WorkplaceNameWithDistance
          workplaceName={workplaceName}
          formattedDistance={formattedDistance}
        />
      )}
    </Stack>
  );
}
